import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  or,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../../Firebase/Firebase.utils";

const COLLECTION = "listings";

// Scope for the CMS view. Intentionally broader than the *public*
// gate: we include listings the CMS has explicitly hidden
// (showOnWebsite === false) so ops can search for them and toggle them
// back on. Listings that never qualified in the first place
// (pipeline < 30% and no showOnWebsite override) stay out.
//
// The public site applies the stricter gate in getPublicListings
// (nextjsprojv1/src/app/utils/firebase.utils.jsx).
const PUBLIC_PIPELINE_STATUS = "30%";

// True iff this listing would render on the public site. Useful for
// tagging hidden ones in the CMS list.
export function isPubliclyVisible(doc) {
  if (!doc) return false;
  if (doc.showOnWebsite === true) return true;
  if (doc.showOnWebsite === false) return false;
  return doc.pipeline_status === PUBLIC_PIPELINE_STATUS;
}

/**
 * Build query constraints for the listings list view.
 *
 * Two shapes:
 *  1. Browse (no search): `orderBy("__name__")`. Always indexed, no
 *     composite-index requirement.
 *  2. Search: case-sensitive prefix match on `property_name` using the
 *     Firestore prefix-range idiom — `where(>=, q)` + `where(<=, q + )`
 *     + `orderBy("property_name")`.
 *
 * Caveats on search:
 *   - Single-field only (Firestore can't OR across fields cheaply).
 *   - Case-sensitive — typing "bgc" won't match "BGC Tower". To make it
 *     case-insensitive, add a `propertyNameLower` field on each listing
 *     in the source CRM and switch the where() to that field. Same
 *     pattern as the `titleLower` field we use on blogs/news.
 *   - Prefix only — typing "Tower" won't match "BGC Tower". For
 *     substring or fuzzy search, an external index (Algolia/Typesense)
 *     would be needed.
 */
function buildConstraints({ search, cursor, pageSize, includeLimit }) {
  const constraints = [
    or(
      where("showOnWebsite", "==", true),
      where("showOnWebsite", "==", false),
      where("pipeline_status", "==", PUBLIC_PIPELINE_STATUS),
    ),
  ];
  const q = (search || "").trim();

  if (q) {
    constraints.push(where("property_name", ">=", q));
    constraints.push(where("property_name", "<=", q + ""));
    constraints.push(orderBy("property_name"));
  } else {
    constraints.push(orderBy("__name__"));
  }

  if (cursor) constraints.push(startAfter(cursor));
  if (includeLimit) constraints.push(limit(pageSize));

  return constraints;
}

/** Aggregate count — 1 read regardless of how many match. */
export async function fetchListingsCount({ search } = {}) {
  const constraints = buildConstraints({ search, includeLimit: false });
  const q = query(collection(db, COLLECTION), ...constraints);
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

/**
 * One-shot fetch of every listing. Used by the hybrid search mode —
 * paid once when the user submits their first search in a session,
 * then filter/paginate client-side. Ordered by `__name__` for a stable,
 * always-indexed sort; consumers re-sort client-side by property name.
 */
export async function fetchAllListings() {
  const q = query(
    collection(db, COLLECTION),
    or(
      where("showOnWebsite", "==", true),
      where("showOnWebsite", "==", false),
      where("pipeline_status", "==", PUBLIC_PIPELINE_STATUS),
    ),
    orderBy("__name__"),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ ...d.data(), id: d.id }));
}

/** One page of listings — pageSize reads. */
export async function fetchListingsPage({
  pageSize = 24,
  cursor = null,
  search,
} = {}) {
  const constraints = buildConstraints({
    search,
    cursor,
    pageSize,
    includeLimit: true,
  });
  const q = query(collection(db, COLLECTION), ...constraints);
  const snap = await getDocs(q);
  return {
    docs: snap.docs.map((d) => ({ ...d.data(), id: d.id })),
    snaps: snap.docs,
  };
}
